import {
  GameSpinTheBottleCampaignLimit,
  GameSpinTheBottleCustomLabelsData,
  GameSpinTheBottleData,
  GameSpinTheBottleGeneralData,
  GameSpinTheBottleSpinButtonPositionType,
  GameSpinTheBottleSpinButtonType,
  GameSpinTheBottleFieldItemData,
  SpinTheBottleInstantWin,
  GameSpinTheBottleAdvancedData
} from '@/components/games/spinthebottle/Data';
import useDevice from '@/hooks/useDevice';
import { GameIndicator, GameModel, GameState } from '@/models/GameModel';
import { DrawType } from '@/typings/enums/enums';
import { GameIndicatorIcon } from '@/components/indicators/Model';

// GameData that needs to be sent after game has ended
// use to secure if a user dont get the right prize
// then we can lookup the gameData and see what the user got.
export interface GameDataSpinTheBottle {
  calculation: number[];
  wheelComputed: Record<string, string>;
  wheelHeight: number;
  wheelWidth: number;
  fieldsOccupied: number[];
  fields: {
    [key: string]: GameSpinTheBottleFieldItemData;
  };
  spinsLeft: number;
  spinsUsed: number;
  winnerFieldId: number | undefined;
  winner: boolean | undefined; // winner only set if instant win is on else its undefined
  data: number | null; // the prize position for the current spin
  position: number;
}

interface GameSpinTheBottleGeneralState {
  bottleImage?: string;
  bottleHeight?: number;
  bottleWidth?: number;
  bottleHeightMobile?: number;
  bottleWidthMobile?: number;
  wheelImage: string;
  numFields: number;
  spins: number;
  delay: number;
  autoSpinDelay?: number;
  spinButton?: GameSpinTheBottleSpinButtonType;
  spinButtonPosition?: GameSpinTheBottleSpinButtonPositionType;
  drawType?: DrawType;
  fields: {
    [key: string]: GameSpinTheBottleFieldItemData;
  };
  fieldsOccupied?: number[];
  campaignLimitInterval?: GameSpinTheBottleCampaignLimit;
  campaignLimit?: string;
}

interface GameSpinTheBottleCustomLabelsState {
  spinButton?: string;
  spinsLeft?: string;
}
export interface GameSpinTheBottleAdvancedSoundState {
  enabled: boolean;
  src?: string;
}

export interface GameSpinTheBottleAdvancedState {
  sound: GameSpinTheBottleAdvancedSoundState;
}

export interface GameSpinTheBottleState extends GameState {
  clickEnabled: boolean;
  general: GameSpinTheBottleGeneralState;
  customLabels?: GameSpinTheBottleCustomLabelsState;
  advanced: GameSpinTheBottleAdvancedState;
  winnerField?: number;
  winnerFieldId?: number;
  elementStyling?: {
    generalClassStyles?: string;
  };
}

export class GameSpinTheBottleModel extends GameModel<GameSpinTheBottleData, GameSpinTheBottleState> {
  parseGame(data: GameSpinTheBottleData): void {
    const state = this.state;
    const { isMobile } = useDevice();

    state.general = GameSpinTheBottleModel.constructGeneralState(data.general);

    if (isMobile) {
      if (data.general.mobile) {
        state.general.bottleImage = data.general.mobile.bottle;
      }
    }

    if (data.general?.fields_occupied && data.general?.fields_occupied.length > 0) {
      state.general.fieldsOccupied = data.general.fields_occupied.map((i: number) => Number(i));
    }

    state.clickEnabled = GameSpinTheBottleSpinButtonType.USE_CLICK === state.general?.spinButton ?? false;

    if (data.customlabels) {
      state.customLabels = GameSpinTheBottleModel.constructCustomLabelsState(data.customlabels);
    }
    state.advanced = state.advanced ?? {};
    state.advanced.sound = GameSpinTheBottleModel.constructAdvancedSoundState(data?.advanced);

    state.elementStyling = state.elementStyling || {};

    state.elementStyling.generalClassStyles = GameSpinTheBottleModel.constructGeneralClassStyles(state.general);
  }

  private static constructAdvancedSoundState(
    data: GameSpinTheBottleAdvancedData | undefined
  ): GameSpinTheBottleAdvancedSoundState {
    return {
      enabled: !!data?.sound,
      ...(data?.sound && { src: data.sound })
    };
  }

  private static constructGeneralClassStyles(state: GameSpinTheBottleGeneralState): string {
    if (!state.bottleHeight || !state.bottleWidth) {
      return '';
    }
    const scale = 120 / state.bottleHeight;
    const width = state.bottleWidth * scale;
    const height = state.bottleHeight * scale;
    const { isMobile } = useDevice();

    if (isMobile) {
      if (state.bottleWidthMobile && state.bottleHeightMobile && state.bottleImage) {
        return `
          .wheel-bottle {
            margin-left:-${state?.bottleWidthMobile / 2}px !important;
            margin-top:-${state?.bottleHeightMobile / 2}px !important;
          }
        `;
      } else {
        return `
           .wheel-bottle {
            margin-left: -${width / 2}px !important;
            margin-top: -${height / 2}px !important;
            width:${width}px;
            height:${height}px;
        `;
      }
    }

    return `
          .wheel-bottle {
            margin-left:${-state.bottleWidth / 2}px !important;
            margin-top:${-state.bottleHeight / 2}px !important;
          }
        `;
  }
  private static constructCustomLabelsState(data: GameSpinTheBottleCustomLabelsData) {
    return {
      ...(data?.spin_button && { spinButton: data.spin_button }),
      ...(data?.spins_left && { spinsLeft: data.spins_left })
    };
  }

  private static constructGeneralState(data: GameSpinTheBottleGeneralData): GameSpinTheBottleGeneralState {
    return {
      ...(data?.bottle && { bottleImage: data.bottle }),
      ...(data?.bottle_height && { bottleHeight: Number(data.bottle_height) }),
      ...(data?.bottle_width && { bottleWidth: Number(data.bottle_width) }),

      ...(data?.bottle_height_mobile && { bottleHeightMobile: Number(data.bottle_height_mobile) }),
      ...(data?.bottle_width_mobile && { bottleWidthMobile: Number(data.bottle_width_mobile) }),
      wheelImage: data.image,
      numFields: Number(data.num_fields),
      spins: Number(data.spins),
      delay: Number(data.delay),
      ...(data?.auto_delay && { autoSpinDelay: Number(data.auto_delay) }),
      ...(data?.spin_button && { spinButton: data.spin_button }),
      ...(data?.spin_button_position && {
        spinButtonPosition: data.spin_button_position
      }),
      ...(data?.draw_type && { drawType: data.draw_type }),
      ...(data?.campaign_limit_interval && {
        campaignLimitInterval: data.campaign_limit_interval
      }),
      ...(data?.campaign_limit && {
        campaignLimit: data.campaign_limit
      }),
      ...(data?.fields && {
        fields: data.fields
      })
    };
  }

  public setInstantWinnerData(data: SpinTheBottleInstantWin) {
    super.setInstantWinnerData(data);
    const winnerField = data['winner_field'];
    const winnerFieldId = data['winner_field_id'];

    if (winnerField) {
      this.state.winnerField = Number(winnerField);
    }

    if (winnerFieldId) {
      this.state.winnerFieldId = Number(winnerFieldId);
    }
  }

  public hasInstantWin(): boolean {
    const state = this.state;
    return state.general.drawType === DrawType.AUTO;
  }

  public getIndicators(): GameIndicator[] {
    if (this.state.general.spins <= 1) {
      return [];
    }
    return [
      {
        indicatorKey: 'spins',
        metricKey: {
          spins_left: 'spins_left',
          spins_used: 'spins_used'
        },
        icon: GameIndicatorIcon.SPINS,
        value: {
          spins_left: this.state.general.spins,
          spins_used: 0
        }
      }
    ];
  }

  public isGameValid(): boolean {
    // is valid logic here
    // const data = this.getData();
    return true;
  }

  authorSignature(): string {
    return 'Sebastian Jakobsen';
  }
}
